import { useEffect, useState } from "react";
import { rememberedSpaceHue } from "@/lib/shellFlat";
import { logoPlateImage } from "@/lib/projectImages";
import { msg, type Msg } from "@shared/i18n";

/**
 * A project's colour — the flat shell's room. Chosen in Project Settings
 * (the project dialog), otherwise taken from the project's logo, otherwise
 * the brand's own purple. Only its HUE is used: the shell fixes saturation
 * and lightness per theme, so every choice reads in light and dark alike.
 * A grey choice (Graphite, or any custom grey) is the one exception: it has
 * no hue, so the room is drawn in greyscale (`"grey"`, lib/shellFlat
 * applySpaceHue). A grey LOGO still falls back to the default purple; only a
 * colour someone picks makes a project grey.
 *
 * The chosen colour is the project's own `space_color` (0112), the same for
 * everyone on the project; null takes it from the logo.
 */

export const PROJECT_COLOURS: ReadonlyArray<{ id: string; label: Msg; hex: string }> = [
  { id: "aubergine", label: msg("projectColourAubergine", "Aubergine"), hex: "#5b3aa6" },
  { id: "harbour", label: msg("projectColourHarbour", "Harbour"), hex: "#2f6fd6" },
  { id: "teal", label: msg("projectColourTeal", "Teal"), hex: "#1f8f95" },
  { id: "forest", label: msg("projectColourForest", "Forest"), hex: "#2f8f6a" },
  { id: "olive", label: msg("projectColourOlive", "Olive"), hex: "#7d8a2e" },
  { id: "gold", label: msg("projectColourGold", "Gold"), hex: "#c28a12" },
  { id: "brick", label: msg("projectColourBrick", "Brick"), hex: "#c0533a" },
  { id: "rose", label: msg("projectColourRose", "Rose"), hex: "#d0457a" },
  { id: "graphite", label: msg("projectColourGraphite", "Graphite"), hex: "#5f6066" },
];

export const DEFAULT_HUE = 256;

/** A room's colour: a hue, or greyscale. */
export type SpaceHue = number | "grey";

export function hexToHue(hex: string): number | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) / 255, g = ((n >> 8) & 255) / 255, b = (n & 255) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
  if (d < 0.04) return null; // grey: no hue to take
  const h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return Math.round((h * 60 + 360) % 360);
}

/** What a chosen colour draws: its hue, "grey" for a grey, null for no colour. */
export function spaceHueOf(hex: string): SpaceHue | null {
  if (!/^#?[0-9a-f]{6}$/i.test(hex.trim())) return null;
  return hexToHue(hex) ?? "grey";
}

/* ---- The logo's colour ----------------------------------------------- */

const logoHues = new Map<string, number | null>();

/** The most-used saturated hue in the logo, read once per image; null for a grey or unreadable one. */
export function useLogoHue(raw: string | null | undefined): number | null {
  const src = raw ? logoPlateImage(raw).src : null;
  const [hue, setHue] = useState<number | null>(() => (src && logoHues.has(src) ? logoHues.get(src)! : null));
  useEffect(() => {
    if (!src) return setHue(null);
    if (logoHues.has(src)) return setHue(logoHues.get(src)!);
    let alive = true;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = () => {
      let found: number | null = null;
      try {
        const size = 48;
        const c = document.createElement("canvas");
        c.width = c.height = size;
        const ctx = c.getContext("2d", { willReadFrequently: true })!;
        ctx.drawImage(img, 0, 0, size, size);
        const px = ctx.getImageData(0, 0, size, size).data;
        const bins = new Array(36).fill(0);
        for (let i = 0; i < px.length; i += 4) {
          if (px[i + 3] < 128) continue;
          const r = px[i] / 255, g = px[i + 1] / 255, b = px[i + 2] / 255;
          const max = Math.max(r, g, b), min = Math.min(r, g, b), d = max - min;
          const l = (max + min) / 2;
          if (d < 0.18 || l < 0.12 || l > 0.92) continue; // greys, near-black, near-white
          const h = max === r ? ((g - b) / d) % 6 : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
          bins[Math.floor(((h * 60 + 360) % 360) / 10)] += d;
        }
        const top = bins.reduce((best, v, i) => (v > bins[best] ? i : best), 0);
        found = bins[top] > 0 ? top * 10 + 5 : null;
      } catch {
        found = null; // a tainted canvas: no colour from this logo
      }
      logoHues.set(src, found);
      if (alive) setHue(found);
    };
    img.onerror = () => {
      logoHues.set(src, null);
      if (alive) setHue(null);
    };
    img.src = src;
    return () => {
      alive = false;
    };
  }, [src]);
  return hue;
}

/** The hue a project's room is drawn in. */
export function useProjectHue(
  project: { id?: string; projectLogo?: string | null; spaceColor?: string | null } | null | undefined,
  projectId?: string | null,
): SpaceHue {
  const chosen = project?.spaceColor ?? null;
  const fromLogo = useLogoHue(project?.projectLogo ?? null);
  // While the project and its logo load, the colour this device last drew it
  // in (lib/shellFlat rememberSpaceHue), so the ground does not flick from
  // the default to the project's own.
  const id = project?.id ?? projectId ?? null;
  const remembered = id ? rememberedSpaceHue(id) : null;
  return (chosen ? spaceHueOf(chosen) : null) ?? fromLogo ?? remembered ?? DEFAULT_HUE;
}
